package com.snakesworld.game.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.snakesworld.game.domain.ArenaPlayerRef
import com.snakesworld.game.domain.NicknameDisplay
import com.snakesworld.game.ui.theme.GameCanvasColors
import com.snakesworld.game.ui.theme.appFontFamily
import org.jetbrains.compose.resources.stringResource
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.cancel
import snakesworld.shared.generated.resources.players_empty
import snakesworld.shared.generated.resources.players_tap_hint
import snakesworld.shared.generated.resources.players_title

/**
 * In-match list of other players. Tapping a row opens report / block.
 * Blocked nicknames are replaced and are not tappable.
 */
@Composable
fun MatchPlayersOverlay(
    players: List<ArenaPlayerRef>,
    blockedPlayerIds: Set<String>,
    hiddenLabel: String,
    canvasColors: GameCanvasColors,
    onPlayerClick: (id: String, rawName: String) -> Unit,
    onDismiss: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xCC000000))
            .clickable(onClick = onDismiss),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .padding(24.dp)
                .widthIn(min = 280.dp, max = 420.dp)
                .fillMaxWidth()
                .background(canvasColors.hudBgColor, RoundedCornerShape(12.dp))
                .border(2.dp, canvasColors.accentColor, RoundedCornerShape(12.dp))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {},
                )
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Text(
                text = stringResource(Res.string.players_title),
                color = canvasColors.accentColor,
                fontSize = 16.sp,
                fontWeight = FontWeight.Black,
                fontFamily = appFontFamily(),
                maxLines = 1,
                softWrap = false,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = stringResource(Res.string.players_tap_hint),
                color = canvasColors.hudTextColor.copy(alpha = 0.8f),
                fontSize = 11.sp,
                fontFamily = appFontFamily(),
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
            if (players.isEmpty()) {
                Text(
                    text = stringResource(Res.string.players_empty),
                    color = canvasColors.hudTextColor,
                    fontSize = 13.sp,
                    fontFamily = appFontFamily(),
                )
            } else {
                LazyColumn(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(max = 320.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    items(players, key = { it.id }) { player ->
                        val blocked = player.id in blockedPlayerIds
                        val shown = NicknameDisplay.visibleName(
                            raw = player.rawName,
                            playerId = player.id,
                            blockedIds = blockedPlayerIds,
                            hiddenLabel = hiddenLabel,
                        )
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(canvasColors.backgroundColor, RoundedCornerShape(6.dp))
                                .border(1.dp, canvasColors.gridColor, RoundedCornerShape(6.dp))
                                .clickable(enabled = !blocked) {
                                    onPlayerClick(player.id, player.rawName)
                                }
                                .padding(horizontal = 10.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            EllipsizedNickname(
                                text = shown,
                                color = if (blocked) {
                                    canvasColors.hudTextColor.copy(alpha = 0.55f)
                                } else {
                                    Color.White
                                },
                                fontSize = 14.sp,
                                modifier = Modifier.fillMaxWidth(),
                            )
                        }
                    }
                }
            }
            TextButton(
                onClick = onDismiss,
                modifier = Modifier.align(Alignment.End),
            ) {
                Text(
                    text = stringResource(Res.string.cancel),
                    color = canvasColors.hudTextColor,
                    fontFamily = appFontFamily(),
                    fontWeight = FontWeight.Bold,
                    maxLines = 1,
                )
            }
        }
    }
}
